import { createSelector } from 'reselect';
import get from 'lodash.get';

import {
    SELECTOR_SYSTEM_DEFAULT,
    APPLICATION_METADATA_DEFAULT,
    APPLICATION_LANGUAGE_DEFAULT,
    APPLICATION_LANGUAGE_FALLBACK_DEFAULT,
    CONTEXT_HELP_METADATA_DEFAULT,
    CONTEXT_HELP_CONTENT_DEFAULT,
    AGENT_INTEGRATION_DEFAULT,
    PATH_SELECTOR_SYSTEM_DEFAULT,
    SELECTOR_SYSTEM_ATTRIBUTES_DEFAULT,
    SELECTOR_SYSTEM_ID_DEFAULT,
    SELECTOR_SYSTEM_CLASS_DEFAULT,
    SELECTOR_SYSTEM_CUSTOM_DEFAULT,
    USER_DEFINITION_DEFAULT,
    ROLE_DEFINITION_DEFAULT,
    IDENTITY_DEFAULT,
    APPLICATION_ACCESS_OWNER_DEFAULT,
    APPLICATION_ACCESS_DEFAULT,
    APPLICATION_ACCESS_ACL_DEFAULT,
    APPLICATION_SYSTEM_DEFAULT,
    APPLICATION_THEME_DEFAULT,
    APPLICATION_THEME_VARIANT_DEFAULT,
    APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT,
    APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT,
    THEME_ID_DEFAULT,
    APPLICATION_COMPANY_DEFAULT
} from 'yeself-common/defaults/ApplicationDefaults';
import {
    TRACKING_DEFAULT,
    CONFUSION_MODEL_DEFAULT,
    CONFUSION_DEFAULT,
    CONFUSION_REACTION_DEFAULT,
    TRACKING_SECURITY_DEFAULT,
} from 'yeself-common/defaults/TrackingDefaults';
import { DEFAULT_USED_LOCALE, DEFAULT_CONTENT_LANGUAGE } from 'yeself-common/ModelConstant';
import { ApplicationJourneyTrackerMetadata } from 'yeself-common/typings/application/Application';

import { DEFAULT_CONSOLE_LANGUAGE } from 'Console/constants/appConstants';
import {
    APPLICATION_DETAIL_SETTINGS_DEFAULT_STATE,
} from 'Console/constants/applicationDetailDefaults';
import { selectApplicationTours } from 'Console/data/applicationDetail/guides/selectors';

import { REQUEST_STATUS } from './types';

export const getApplicationDetail = (state) => state.applicationDetail;

export const selectApplicationSettings = createSelector(
    [getApplicationDetail],
    ({ settings = APPLICATION_DETAIL_SETTINGS_DEFAULT_STATE }) => settings
);

export const selectAvailableRolloutPolicies = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.availableRolloutPolicies.data
);

export const selectAvailableRolloutPoliciesFetchStatus = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.availableRolloutPolicies.fetchStatus
);

export const selectAvailableRolloutPoliciesReason = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.availableRolloutPolicies.reason
);

export const selectRolloutPoliciesBySpaceFetchStatus = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.rolloutPoliciesBySpace.fetchStatus
);

export const selectRolloutPoliciesBySpaceReason = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.rolloutPoliciesBySpace.reason
);

export const selectExtensionUrls = createSelector(
    [selectApplicationSettings],
    ({ rolloutPolicies }) => rolloutPolicies.extensionUrls
);

export const selectApplicationSettingsUpdateStatus = createSelector(
    [selectApplicationSettings],
    ({ updateStatus = REQUEST_STATUS.IDLE }) => updateStatus
);

export const selectApplicationSettingsData = createSelector(
    [selectApplicationSettings],
    ({ data = {} }) => data
);

export const selectApplicationHealth = createSelector([selectApplicationSettingsData], () => ({
    credentials: {
        vaultReferences: [
            {
                key: '123123123',
            },
        ],
    },
    loginPage: {
        pageLocation: {
            url: 'https://yeself.com',
        },
        passwordFieldSelector: {
            pathDefinition: 'password selector',
        },
        usernameFieldSelector: {
            pathDefinition: 'username selector',
        },
    },
    temporalHealthCredentialStorage: [
        {
            key: '1123',
            password: 'testik',
            username: 'Seges',
        },
    ],
}));
export const selectIsApplicationSettingsLoading = createSelector(
    [selectApplicationSettings],
    ({ isLoading = false }) => isLoading
);

export const selectIsContentLanguageInitialized = createSelector(
    [selectApplicationSettings],
    ({ isContentLanguageInitialized = false }) => isContentLanguageInitialized
);

export const selectIsApplicationActive = createSelector(
    [selectApplicationSettings],
    ({ active = true }) => active
);

export const selectApplicationMetaData = createSelector(
    [selectApplicationSettingsData],
    ({ metadata = APPLICATION_METADATA_DEFAULT }) => metadata
);

export const selectApplicationMetaDataLanguage = createSelector(
    [selectApplicationMetaData],
    ({ language = APPLICATION_LANGUAGE_DEFAULT }) => language
);

export const selectApplicationMetaDataLanguageUsedLocales = createSelector(
    selectApplicationMetaDataLanguage,
    ({ usedLocales = [DEFAULT_USED_LOCALE] }) => usedLocales
);

export const selectFallbackLanguage = createSelector(
    [selectApplicationMetaDataLanguage],
    (language = APPLICATION_LANGUAGE_FALLBACK_DEFAULT) => language.fallbackLanguage
);

export const selectFallbackLanguageCode = createSelector(
    [selectFallbackLanguage],
    (fallbackLanguage = DEFAULT_CONTENT_LANGUAGE) => fallbackLanguage.code
);

export const selectFallbackLanguageOptions = createSelector(
    [selectApplicationMetaDataLanguageUsedLocales, selectFallbackLanguageCode],
    (usedLocales = [DEFAULT_USED_LOCALE], fallbackLanguageCode = DEFAULT_CONTENT_LANGUAGE) => {
        const locale = usedLocales.find((locale) => locale.code === fallbackLanguageCode);
        return {
            id: locale ? locale.code : DEFAULT_CONSOLE_LANGUAGE.code,
            name: locale ? locale.label : DEFAULT_CONSOLE_LANGUAGE.label,
        };
    }
);

export const selectContextHelp = createSelector(
    [selectApplicationMetaData],
    ({ contextHelp = CONTEXT_HELP_METADATA_DEFAULT }) => contextHelp
);

export const selectContextHelpContent = createSelector(
    [selectContextHelp],
    ({ content = CONTEXT_HELP_CONTENT_DEFAULT }) => content
);

export const selectContextHelpList = createSelector([selectContextHelp], ({ list = [] }) => list);

export const selectEnabledContextHelpTours = createSelector(selectApplicationTours, (tours = []) =>
    tours.filter((guide) => get(guide, 'graph.metadata.contextHelp.enabled'))
);

export const selectLanguageList = createSelector(
    [selectApplicationSettings],
    ({ languageList = { isLoading: false, data: [], hasError: false } }) => languageList
);

export const selectLanguageListData = createSelector([selectLanguageList], ({ data = [] }) => data);

export const selectLanguageListIsLoading = createSelector(
    [selectLanguageList],
    ({ isLoading = false }) => isLoading
);

export const selectLanguageListHasError = createSelector(
    [selectLanguageList],
    ({ hasError = false }) => hasError
);

export const selectUsersAccess = createSelector(
    [selectApplicationSettings],
    ({ usersAccess = { isLoading: false, data: [], hasError: false } }) => usersAccess
);

export const selectUsersAccessData = createSelector([selectUsersAccess], ({ data = [] }) => data);

export const selectUsersAccessIsLoading = createSelector(
    [selectUsersAccess],
    ({ isLoading = false }) => isLoading
);

export const selectUsersAccessHasError = createSelector(
    [selectUsersAccess],
    ({ hasError = false }) => hasError
);

export const selectOriginUrls = createSelector(
    [selectApplicationSettingsData],
    ({ originUrls = [] }) => originUrls
);

export const selectTrutedDomains = createSelector(
    [selectApplicationSettingsData],
    ({ trustedDomains = [] }) => trustedDomains
);

export const selectAppId = createSelector([selectApplicationSettingsData], ({ id = '' }) => id);

export const selectOrgId = createSelector(
    [selectApplicationSettingsData],
    ({ organizationId = '' }) => organizationId
);

export const selectApplicationTracking = createSelector(
    selectApplicationMetaData,
    ({ tracking = TRACKING_DEFAULT }) => tracking
);

export const selectApplicationTrackingSecurity = createSelector(
    selectApplicationTracking,
    ({ security = TRACKING_SECURITY_DEFAULT }) => security
);

export const selectApplicationTrackingSecurityHashUserId = createSelector(
    selectApplicationTrackingSecurity,
    ({ hashUserId = false }) => hashUserId
);

export const selectApplicationConfusionModel = createSelector(
    selectApplicationTracking,
    ({ model = CONFUSION_MODEL_DEFAULT }) => model
);

export const selectApplicationConfusion = createSelector(
    selectApplicationTracking,
    ({ confusion = CONFUSION_DEFAULT }) => confusion
);

export const selectApplicationConfusionReaction = createSelector(
    selectApplicationConfusion,
    ({ reaction = CONFUSION_REACTION_DEFAULT }) => reaction
);

export const selectApplicationEditorSettings = createSelector(
    [selectApplicationSettingsData],
    (settings) => (settings.editorSettings ? settings.editorSettings : undefined)
);

export const selectApplicationSelectorSystem = createSelector(
    [selectApplicationSettingsData],
    ({ selectorSystem = SELECTOR_SYSTEM_DEFAULT }) => selectorSystem
);

export const selectApplicationSelectorSystemExcludedLocations = createSelector(
    [selectApplicationSelectorSystem],
    ({ excludedLocations = [] }) => excludedLocations
);

export const selectApplicationSelectorDisabledLocationPaths = createSelector(
    [selectApplicationSelectorSystem],
    ({ disabledLocationPaths = SELECTOR_SYSTEM_DEFAULT.disabledLocationPaths }) => disabledLocationPaths
);

export const selectApplicationSelectorSystemClassOnly = createSelector(
    [selectApplicationSelectorSystem],
    ({ classOnly = PATH_SELECTOR_SYSTEM_DEFAULT }) => classOnly
);

export const selectApplicationSelectorSystemClassOnlyPattern = createSelector(
    [selectApplicationSelectorSystemClassOnly],
    ({ pattern = '' }) => pattern
);

export const selectApplicationSelectorSystemClassOnlyReplacer = createSelector(
    [selectApplicationSelectorSystemClassOnly],
    ({ replace = '' }) => replace
);

export const selectApplicationSelectorSystemAttributes = createSelector(
    [selectApplicationSelectorSystem],
    ({ attributes = SELECTOR_SYSTEM_ATTRIBUTES_DEFAULT }) => attributes
);

export const selectApplicationSelectorSystemId = createSelector(
    [selectApplicationSelectorSystemAttributes],
    ({ id = SELECTOR_SYSTEM_ID_DEFAULT }) => id
);

export const selectApplicationSelectorSystemIdBlacklist = createSelector(
    [selectApplicationSelectorSystemId],
    ({ blacklist = '' }) => blacklist
);

export const selectApplicationSelectorSystemClassName = createSelector(
    [selectApplicationSelectorSystemAttributes],
    ({ className = SELECTOR_SYSTEM_CLASS_DEFAULT }) => className
);

export const selectApplicationSelectorSystemClassNameBlacklist = createSelector(
    [selectApplicationSelectorSystemClassName],
    ({ blacklist = '' }) => blacklist
);

export const selectApplicationSelectorSystemCustom = createSelector(
    [selectApplicationSelectorSystemAttributes],
    ({ custom = SELECTOR_SYSTEM_CUSTOM_DEFAULT }) => custom
);

export const selectApplicationSelectorSystemCustomWhitelist = createSelector(
    [selectApplicationSelectorSystemCustom],
    ({ whitelist = '' }) => whitelist
);

export const selectApplicationIdentity = createSelector([selectApplicationSettingsData], (settings) =>
    settings.identity ? settings.identity : IDENTITY_DEFAULT
);

export const selectApplicationUserDefinition = createSelector(
    [selectApplicationIdentity],
    ({ user = USER_DEFINITION_DEFAULT }) => user
);

export const selectApplicationUserDefinitionSourceType = createSelector(
    [selectApplicationUserDefinition],
    ({ sourceType = '' }) => sourceType
);

export const selectApplicationUserDefinitionValuePath = createSelector(
    [selectApplicationUserDefinition],
    ({ valuePath = '' }) => valuePath
);

export const selectApplicationRoleDefinition = createSelector(
    [selectApplicationIdentity],
    ({ role = ROLE_DEFINITION_DEFAULT }) => role
);

export const selectApplicationTheme = createSelector(
    [selectApplicationSettingsData],
    ({ theme = APPLICATION_THEME_DEFAULT }) => theme
);

export const selectApplicationSelectedTheme = createSelector(
    [selectApplicationTheme],
    ({ selectedTheme = THEME_ID_DEFAULT }) => selectedTheme
);

export const selectApplicationThemeVariant = createSelector(
    [selectApplicationTheme],
    ({ themeVariant = APPLICATION_THEME_VARIANT_DEFAULT }) => themeVariant
);

export const selectApplicationThemeVariantEnabled = createSelector(
    [selectApplicationThemeVariant],
    ({ enabled = false }) => enabled
);

export const selectApplicationThemeVariantMainDefinition = createSelector(
    [selectApplicationThemeVariant],
    ({ main = APPLICATION_THEME_VARIANT_MAIN_DEFINITION_DEFAULT }) => main
);

export const selectApplicationThemeVariantFallback = createSelector(
    [selectApplicationThemeVariant],
    ({ fallback = APPLICATION_THEME_VARIANT_FALLBACK_DEFINITION_DEFAULT }) => fallback
);

export const selectApplicationFallbackVariantEnabled = createSelector(
    [selectApplicationThemeVariantFallback],
    ({ enabled = false }) => enabled
);

export const selectApplicationFallbackVariantValuePath = createSelector(
    [selectApplicationThemeVariantFallback],
    ({ valuePath = '' }) => valuePath
);

export const selectApplicationRoleDefinitionSourceType = createSelector(
    [selectApplicationRoleDefinition],
    ({ sourceType = '' }) => sourceType
);

export const selectApplicationRoleDefinitionValuePath = createSelector(
    [selectApplicationRoleDefinition],
    ({ valuePath = '' }) => valuePath
);

export const selectApplicationName = createSelector(
    [selectApplicationSettingsData],
    (data = {}) => data.name || ''
);

export const selectApplicationLocalization = createSelector(
    [selectApplicationSettingsData],
    (settings) => (settings.localization ? settings.localization : undefined)
);

export const selectApplicationMultiTenant = createSelector([selectApplicationSettingsData], (settings) =>
    settings.multiTenant ? settings.multiTenant : undefined
);

export const selectApplicationAccess = createSelector(
    [selectApplicationSettingsData],
    ({ access = APPLICATION_ACCESS_DEFAULT }) => access
);

export const selectApplicationAccessAcl = createSelector(
    [selectApplicationAccess],
    ({ acl = APPLICATION_ACCESS_ACL_DEFAULT }) => acl
);

export const selectApplicationAccessOwner = createSelector(
    [selectApplicationAccess],
    ({ owner = APPLICATION_ACCESS_OWNER_DEFAULT }) => owner
);

export const selectApplicationSystem = createSelector(
    [selectApplicationSettingsData],
    ({ system = APPLICATION_SYSTEM_DEFAULT }) => system
);

export const selectCompanySystem = createSelector(
    [selectApplicationSettingsData],
    ({ company = APPLICATION_COMPANY_DEFAULT }) => company
);

export const selectCompanySystemId = createSelector([selectCompanySystem], ({ id }) => id);

export const selectApplicationJourneyTracker = createSelector(
    [selectApplicationSettingsData],
    (settings) => get(settings, 'metadata.journeyTracker')
);

export const selectCompanyId = createSelector([selectApplicationSettingsData], (settings) =>
    get(settings, 'company.id')
);

export const selectEnabledJourneys = createSelector(selectApplicationJourneyTracker, (journeyTracker) =>
    journeyTracker
        ? Object.values(journeyTracker).filter(
              (journey: ApplicationJourneyTrackerMetadata) => journey.enabledInContextHelp
          )
        : []
);

export const selectDisabledJourneys = createSelector(selectApplicationJourneyTracker, (journeyTracker) =>
    journeyTracker
        ? Object.values(journeyTracker).filter(
              (journey: ApplicationJourneyTrackerMetadata) => !journey.enabledInContextHelp
          )
        : []
);

export const selectAddOnsAvailableList = createSelector(
    selectApplicationSettings,
    ({ addonsAvailable }) => addonsAvailable.data
);

export const selectAddOnsAvailableListIsLoading = createSelector(
    selectApplicationSettings,
    ({ addonsAvailable }) => addonsAvailable.fetchStatus === REQUEST_STATUS.PENDING
);

export const selectAddonsAvailableListFetchStatus = createSelector(
    selectApplicationSettings,
    ({ addonsAvailable }) => addonsAvailable.fetchStatus
);

export const selectAddonsInstalledListFetchStatus = createSelector(
    selectApplicationSettings,
    ({ addonsInstalled }) => addonsInstalled.fetchStatus
);

export const selectAddonsAvailableListReason = createSelector(
    selectApplicationSettings,
    ({ addonsAvailable }) => addonsAvailable.reason
);

export const selectAddonsInstalledListReason = createSelector(
    selectApplicationSettings,
    ({ addonsInstalled }) => addonsInstalled.reason
);

export const selectAddOnsInstalledList = createSelector(
    selectApplicationSettings,
    ({ addonsInstalled }) => addonsInstalled.data
);

export const selectAddons = createSelector(
    [selectAddOnsAvailableList, selectAddOnsInstalledList],
    (available = [], installed = []) =>
        available.map((addon) => {
            const installedAddon = installed.find(
                (installedAddon) => addon.descriptor.systemKey === installedAddon.addon_system_key
            );

            return {
                ...addon,
                ...(installedAddon && { configuration: installedAddon.configuration }),
                ...(installedAddon && { resource_id: installedAddon.resource_id }),
                ...(installedAddon && { addon_id: installedAddon.addon_id }),
                ...(installedAddon && {
                    addon_instance_system_key: installedAddon.system_key,
                }),
                isInstalled: Boolean(installedAddon),
            };
        })
);

export const selectSettingsAddons = createSelector(
    [selectApplicationSettings],
    ({ addons = {} }) => addons
);

export const selectSelectedAddonInstanceSystemKey = createSelector(
    [selectSettingsAddons],
    ({ selected }) => selected
);

export const selectSelectedAddon = createSelector(
    [selectAddons, selectSelectedAddonInstanceSystemKey],
    (addons, selectedAddonInstanceSystemKey) =>
        addons.find(
            ({ addon_instance_system_key }) =>
                addon_instance_system_key === selectedAddonInstanceSystemKey
        )
);

export const selectLanguageAddOnsInstalledList = createSelector(
    [selectAddOnsInstalledList, selectAppId],
    (addons = []) => addons.filter(({ addon }) => addon.modules.language)
);

export const selectAddOnsInstalledListIsLoading = createSelector(
    selectApplicationSettings,
    (settings) => settings.addonsInstalled.isLoading
);

export const selectApplicationIntegration = createSelector(
    [selectApplicationMetaData],
    ({ integration = APPLICATION_METADATA_DEFAULT.integration }) => integration
);

export const selectApplicationIntegrationAgentSettings = createSelector(
    [selectApplicationIntegration],
    ({ agent = AGENT_INTEGRATION_DEFAULT }) => agent
);

export const selectIsInternetExplorerEnabled = createSelector(
    [selectApplicationIntegrationAgentSettings],
    (settings = AGENT_INTEGRATION_DEFAULT) => settings.internetExplorer
);

export const selectIsPopupsEnabled = createSelector(
    [selectApplicationIntegrationAgentSettings],
    (settings = AGENT_INTEGRATION_DEFAULT) => settings.popups
);

export const selectIsStartFromIframeEnabled = createSelector(
    [selectApplicationIntegrationAgentSettings],
    (settings = AGENT_INTEGRATION_DEFAULT) => settings.startFromIFrame
);

export const selectIsCustomScriptEnabled = createSelector(
    [selectApplicationIntegrationAgentSettings],
    (settings = AGENT_INTEGRATION_DEFAULT) => settings.customScript
);

export const selectIsIfcNamespaceEnabled = createSelector(
    [selectApplicationIntegrationAgentSettings],
    (settings = AGENT_INTEGRATION_DEFAULT) => settings.ifcNamespace
);

export const selectSystemPagesV2Enabled = createSelector(
    [selectApplicationSettingsData],
    ({ systemPagesV2Enabled = false }) => systemPagesV2Enabled
);
